import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { APP_URL, CONTACT_URL } from "../content";
import { track } from "../lib/analytics";

export default function Navbar({ lang, setLang, t }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isLanding = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleLang = () => setLang(lang === "ES" ? "EN" : "ES");
  const handleContactClick = () => track("contact_click");
  const handleSignupClick = () => track("signup_click");

  const handleAnchor = (e, id) => {
    if (isLanding) {
      e.preventDefault();
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      setOpen(false);
    } else {
      // Cross-route: let react-router navigate, then scroll
      e.preventDefault();
      setOpen(false);
      navigate(`/#${id}`);
      window.setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }
  };

  const nav = [
    { id: "platform", label: t.nav.platform },
    { id: "plans", label: t.nav.plans },
    { id: "news", label: t.nav.news },
  ];

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#030508]/85 border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center gap-3 group"
        >
          <span className="relative inline-flex h-9 w-9 items-center justify-center border border-white/15 group-hover:border-[var(--primary)] transition-colors">
            <img
              src="/images/isotipo_small_amarillo.png"
              alt=""
              aria-hidden="true"
              width="16"
              height="16"
              decoding="async"
              className="h-4 w-4 object-contain"
            />
            <span className="corner-tl !opacity-100" />
            <span className="corner-br !opacity-100" />
          </span>
          <div className="leading-none">
            <div className="font-display text-sm md:text-base font-extrabold tracking-tight">
              SCOPELINK
              <span className="text-[var(--primary)]">.IO</span>
            </div>
            <div className="font-mono text-[9px] tracking-[0.3em] text-white/40 mt-1">
              OBSERVATORY · IoT
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-10">
          {nav.map((item) => (
            <a
              key={item.id}
              href={`/#${item.id}`}
              onClick={(e) => handleAnchor(e, item.id)}
              data-testid={`nav-link-${item.id}`}
              className="font-mono text-xs uppercase tracking-[0.25em] text-white/70 hover:text-[var(--primary)] transition-colors"
            >
              {item.label}
            </a>
          ))}
          <a
            key="contact"
            href={CONTACT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-link-contact"
            onClick={handleContactClick}
            className="font-mono text-xs uppercase tracking-[0.25em] text-white/70 hover:text-[var(--primary)] transition-colors"
          >
            {t.nav.contact}
          </a>
        </nav>

        <div className="flex items-center gap-3 md:gap-5">
          {/* Lang */}
          <button
            data-testid="lang-toggle"
            onClick={toggleLang}
            className="hidden sm:flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-white/60 hover:text-[var(--primary)] transition-colors border border-white/10 hover:border-[var(--primary)]/40 px-3 py-2"
            aria-label="Toggle language"
          >
            <span className={lang === "ES" ? "text-[var(--primary)]" : ""}>ES</span>
            <span className="text-white/20">/</span>
            <span className={lang === "EN" ? "text-[var(--primary)]" : ""}>EN</span>
          </button>

          {/* CTA */}
          <a
            href={CONTACT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-cta-contact"
            onClick={handleContactClick}
            className="hidden md:inline-flex items-center gap-2 bg-[var(--primary)] text-black font-mono text-[11px] tracking-[0.25em] uppercase font-semibold px-5 py-3 hover:bg-[var(--primary-hover)] transition-colors"
          >
            {t.nav.contact}
            <span aria-hidden>→</span>
          </a>
          <a
            href={APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-cta-app-access"
            onClick={handleSignupClick}
            className="hidden md:inline-flex items-center gap-2 border border-white/15 text-white font-mono text-[11px] tracking-[0.25em] uppercase font-semibold px-5 py-3 hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
          >
            {t.nav.appAccess}
          </a>

          {/* Mobile burger */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden inline-flex items-center justify-center h-10 w-10 border border-white/15 text-white"
            onClick={() => setOpen((o) => !o)}
            aria-label="Open menu"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t border-white/5 bg-[#030508]/95 backdrop-blur-xl"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {nav.map((item) => (
              <a
                key={item.id}
                href={`/#${item.id}`}
                onClick={(e) => handleAnchor(e, item.id)}
                data-testid={`mobile-nav-link-${item.id}`}
                className="font-mono text-sm uppercase tracking-[0.2em] text-white/80 hover:text-[var(--primary)]"
              >
                {item.label}
              </a>
            ))}
            <a
              href={CONTACT_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-nav-link-contact"
              onClick={() => {
                handleContactClick();
                setOpen(false);
              }}
              className="font-mono text-sm uppercase tracking-[0.2em] text-white/80 hover:text-[var(--primary)]"
            >
              {t.nav.contact}
            </a>
            <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-white/5">
              <button
                data-testid="mobile-lang-toggle"
                onClick={toggleLang}
                className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/60"
              >
                <span className={lang === "ES" ? "text-[var(--primary)]" : ""}>ES</span>
                <span className="text-white/20"> / </span>
                <span className={lang === "EN" ? "text-[var(--primary)]" : ""}>EN</span>
              </button>
              <a
                href={CONTACT_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-cta-contact"
                onClick={handleContactClick}
                className="bg-[var(--primary)] text-black font-mono text-[11px] tracking-[0.25em] uppercase font-semibold px-4 py-2.5"
              >
                {t.nav.contact} →
              </a>
              <a
                href={APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-cta-app-access"
                onClick={handleSignupClick}
                className="border border-white/15 text-white font-mono text-[11px] tracking-[0.25em] uppercase font-semibold px-4 py-2.5"
              >
                {t.nav.appAccess}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
